/**
 * DocumentAnalyzer - Felles komponent for dokumentanalyse på tvers av moduler
 * Støtter arbeidskontrakter, leiekontrakter og andre juridiske dokumenter
 * @version 1.0.0 - MASTERPROMPT v2.0 compliant (Fase 3 - Deduplisering)
 */

import React, { useState, useCallback } from 'react';
import {
  Upload, FileText, AlertTriangle, CheckCircle, XCircle,
  Loader2, Scale, Info, ChevronDown, ChevronUp, Sparkles,
  HardDrive, FolderOpen, Eye, Shield, Brain, Zap, Copy, Check
} from 'lucide-react';
import mammoth from 'mammoth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// TYPES & CONFIGS
// ============================================================

// Severity colors
const SEVERITY_CONFIG = {
  kritisk: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20' },
  høy: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-500/20' },
  middels: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/20' },
  info: { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', badge: 'bg-gray-500/20' }
};

const SCORE_CONFIG = {
  kritisk: { label: 'Kritiske problemer', color: 'red', icon: XCircle },
  advarsel: { label: 'Må vurderes', color: 'yellow', icon: AlertTriangle },
  ok: { label: 'Generelt OK', color: 'green', icon: CheckCircle },
  god: { label: 'Ingen problemer funnet', color: 'green', icon: CheckCircle }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

// Find context around match
const findContext = (content, match, contextLength = 100) => {
  const index = content.indexOf(match);
  if (index === -1) return '';
  
  const start = Math.max(0, index - contextLength);
  const end = Math.min(content.length, index + match.length + contextLength);
  
  let context = content.slice(start, end);
  if (start > 0) context = '...' + context;
  if (end < content.length) context = context + '...';
  
  return context;
};

// Run local regex-based analysis
const runLocalAnalysis = (content, rules, positiveRules) => {
  const findings = [];
  const positives = [];
  
  // Check problematic clauses
  rules.forEach(rule => {
    const matches = content.match(rule.regex);
    
    if (matches) {
      // Check negative regex (exclusion)
      if (rule.negativRegex) {
        const hasNegative = content.match(rule.negativRegex);
        if (hasNegative) return;
      }
      
      findings.push({
        ...rule,
        matches,
        context: findContext(content, matches[0])
      });
    }
  });
  
  // Check positive elements
  positiveRules.forEach(rule => {
    if (content.match(rule.regex)) {
      positives.push(rule.title);
    }
  });
  
  // Calculate score
  const critical = findings.filter(f => f.severity === 'kritisk').length;
  const high = findings.filter(f => f.severity === 'høy').length;
  const medium = findings.filter(f => f.severity === 'middels').length;
  
  let score;
  if (critical > 0) {
    score = { value: 'kritisk', ...SCORE_CONFIG.kritisk };
  } else if (high > 0) {
    score = { value: 'advarsel', ...SCORE_CONFIG.advarsel };
  } else if (medium > 0) {
    score = { value: 'ok', ...SCORE_CONFIG.ok };
  } else {
    score = { value: 'god', ...SCORE_CONFIG.god };
  }
  
  return {
    findings,
    positives,
    score,
    stats: { critical, high, medium }
  };
};

// ============================================================
// SUB-COMPONENTS
// ============================================================

// Expandable finding card
function FindingCard({ finding, expanded, onToggle }) {
  const severity = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.info;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${severity.border}`}>
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between bg-[var(--bg-highlight)] hover:bg-[var(--bg-main)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${severity.badge} ${severity.text}`}>
            {finding.severity?.toUpperCase() || 'INFO'}
          </span>
          <span className="font-medium text-[var(--text-main)] text-left">{finding.title}</span>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />
        )}
      </button>
      
      {expanded && (
        <div className="p-4 border-t border-[var(--border-subtle)] space-y-3">
          <div>
            <p className="text-sm font-medium text-[var(--text-main)] mb-1">Forklaring:</p>
            <p className="text-sm text-[var(--text-muted)]">{finding.explanation}</p>
          </div>
          
          {finding.lawRef && (
            <div>
              <p className="text-sm font-medium text-[var(--text-main)] mb-1">Lovhenvisning:</p>
              <p className="text-sm text-[var(--copper)]">{finding.lawRef}</p>
            </div>
          )}
          
          {finding.context && (
            <div>
              <p className="text-sm font-medium text-[var(--text-main)] mb-1 flex items-center gap-1">
                <Eye className="w-3 h-3" />
                Funnet i kontekst:
              </p>
              <div className="p-2 bg-[var(--bg-main)] rounded text-xs text-[var(--text-muted)] font-mono">
                {finding.context}
              </div>
            </div>
          )}
          
          {finding.recommendation && (
            <div>
              <p className="text-sm font-medium text-[var(--text-main)] mb-1 flex items-center gap-1">
                <Zap className="w-3 h-3 text-[var(--copper)]" />
                Anbefaling:
              </p>
              <p className="text-sm text-[var(--success)]">{finding.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Score display
function ScoreDisplay({ score, stats }) {
  const ScoreIcon = score.icon || CheckCircle;
  const colorClass = score.color === 'red' ? 'bg-red-500/10 border-red-500/30' :
                     score.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/30' :
                     'bg-[var(--success)]/10 border-[var(--success)]/30';
  const iconColor = score.color === 'red' ? 'text-red-500' :
                    score.color === 'yellow' ? 'text-yellow-500' :
                    'text-[var(--success)]';
  
  return (
    <div className={`p-4 rounded-lg border ${colorClass}`}>
      <div className="flex items-center gap-3">
        <ScoreIcon className={`w-6 h-6 ${iconColor}`} />
        <div>
          <p className="font-bold text-[var(--text-main)]">{score.label}</p>
          <p className="text-sm text-[var(--text-muted)]">
            {stats.critical > 0 && `${stats.critical} kritiske, `}
            {stats.high > 0 && `${stats.high} høy prioritet, `}
            {stats.medium > 0 && `${stats.medium} middels`}
            {stats.critical === 0 && stats.high === 0 && stats.medium === 0 && 'Ingen problematiske klausuler identifisert'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function DocumentAnalyzer({
  // Module configuration
  moduleId,
  title = 'Dokumentanalyse',
  subtitle = 'Last opp et dokument for analyse',
  
  // Analysis rules
  rules = [],
  positiveRules = [],
  
  // AI Configuration
  aiEndpoint = '/api/contract-analysis/analyze',
  enableAI = true,
  
  // Library state (for local storage)
  libraryReady = false,
  onSetupLibrary,
  
  // Callbacks
  onAnalysisComplete,
  
  // Recommendations to show
  recommendations = [
    'Kontakt advokat ved kritiske funn',
    'Be om skriftlig forklaring på uklare punkter før signering',
    'Ta deg tid – du har rett til betenkningstid',
    'Denne analysen erstatter ikke juridisk rådgivning'
  ]
}) {
  // State
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [localResult, setLocalResult] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [showAiResult, setShowAiResult] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Handle file upload
  const handleFileChange = useCallback(async (event) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setLocalResult(null);
    setAiResult(null);
    setShowAiResult(false);
    setAnalyzing(true);
    
    try {
      let content = '';
      
      if (uploadedFile.name.endsWith('.docx')) {
        const arrayBuffer = await uploadedFile.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        content = result.value;
      } else if (uploadedFile.name.endsWith('.txt')) {
        content = await uploadedFile.text();
      } else {
        throw new Error('Filtypen støttes ikke. Bruk .docx eller .txt');
      }
      
      setText(content);
      
      // Run local analysis
      const result = runLocalAnalysis(content, rules, positiveRules);
      setLocalResult(result);
      
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    } catch (error) {
      console.error('Feil ved lesing av fil:', error);
      setLocalResult({
        error: error.message,
        findings: [],
        positives: []
      });
    } finally {
      setAnalyzing(false);
    }
  }, [rules, positiveRules, onAnalysisComplete]);
  
  // Run AI analysis
  const runAiAnalysis = useCallback(async () => {
    if (!text || !enableAI) return;
    
    setAiAnalyzing(true);
    setAiResult(null);
    
    try {
      const token = sessionStorage.getItem('jm_token');
      if (!token) {
        throw new Error('Ikke innlogget. Vennligst logg inn på nytt.');
      }
      
      const response = await fetch(`${API_URL}${aiEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: text,
          filename: file?.name,
          module: moduleId
        })
      });
      
      if (!response.ok) {
        let errorMessage = 'AI-analyse feilet';
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {
          if (response.status === 401) {
            errorMessage = 'Ikke innlogget. Vennligst logg inn på nytt.';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      setAiResult(data);
      setShowAiResult(true);
    } catch (error) {
      console.error('AI-analyse feil:', error);
      setAiResult({ error: error.message });
    } finally {
      setAiAnalyzing(false);
    }
  }, [text, file, enableAI, aiEndpoint, moduleId]);
  
  // Toggle expanded
  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  // Copy results
  const copyResults = () => {
    if (!localResult) return;
    
    const textOutput = `
DOKUMENTANALYSE - RettVerk (${title})
==========================
Score: ${localResult.score?.label || 'Ukjent'}
${localResult.stats?.critical || 0} kritiske | ${localResult.stats?.high || 0} høy | ${localResult.stats?.medium || 0} middels

FUNN
${localResult.findings.map(f => `- [${f.severity?.toUpperCase()}] ${f.title}: ${f.explanation}`).join('\n')}

POSITIVE ELEMENTER
${localResult.positives.join(', ')}

Generert av RettVerk - ${new Date().toLocaleDateString('nb-NO')}
    `.trim();
    
    navigator.clipboard.writeText(textOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Reset
  const reset = () => {
    setFile(null);
    setText('');
    setLocalResult(null);
    setAiResult(null);
    setExpandedItems({});
    setShowAiResult(false);
  };
  
  return (
    <div className="space-y-6" data-testid={`${moduleId}-document-analyzer`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="w-6 h-6 text-[var(--copper)]" />
          <div>
            <h2 className="text-lg font-bold text-[var(--text-main)]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {title}
            </h2>
            <p className="text-xs text-[var(--text-muted)]">{subtitle}</p>
          </div>
        </div>
        
        {onSetupLibrary && !libraryReady ? (
          <button
            onClick={onSetupLibrary}
            className="flex items-center gap-2 px-3 py-1.5 bg-[var(--copper)]/10 text-[var(--copper)] rounded-lg text-sm"
          >
            <FolderOpen className="w-4 h-4" />
            Velg mappe
          </button>
        ) : (
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-[var(--success)]/10 border border-[var(--success)]/30">
            <HardDrive className="w-3 h-3 text-[var(--success)]" />
            <span className="text-xs text-[var(--success)]">Lokal lagring</span>
          </div>
        )}
      </div>
      
      {/* Upload Area */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-6">
        <div className="flex flex-col items-center justify-center">
          <label className="cursor-pointer w-full">
            <input
              type="file"
              accept=".docx,.txt,.jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-upload-input"
            />
            
            <div className="border-2 border-dashed border-[var(--border-subtle)] rounded-lg p-8 text-center hover:border-[var(--copper)]/50 transition-colors">
              {analyzing ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-10 h-10 text-[var(--copper)] animate-spin mb-4" />
                  <p className="text-[var(--text-main)]">Analyserer dokument...</p>
                </div>
              ) : file ? (
                <div className="flex flex-col items-center">
                  <FileText className="w-10 h-10 text-[var(--copper)] mb-4" />
                  <p className="text-[var(--text-main)] font-medium">{file.name}</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Klikk for å velge en annen fil</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-10 h-10 text-[var(--text-muted)] mb-4" />
                  <p className="text-[var(--text-main)] font-medium">Last opp dokument</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Støtter .docx og .txt filer</p>
                </div>
              )}
            </div>
          </label>
        </div>
        
        <div className="mt-4 p-3 bg-[var(--info)]/10 border border-[var(--info)]/30 rounded-lg">
          <div className="flex gap-2">
            <Shield className="w-4 h-4 text-[var(--info)] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[var(--text-main)]">
              <strong>Privacy-first:</strong> Grunnleggende analyse gjøres lokalt. 
              AI-analyse (valgfritt) bruker kryptert tilkobling.
            </p>
          </div>
        </div>
      </div>
      
      {/* AI Analysis Button */}
      {enableAI && localResult && !localResult.error && !aiResult && (
        <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Brain className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-medium text-[var(--text-main)]">Dyp AI-analyse</h3>
                <p className="text-sm text-[var(--text-muted)]">Få juridisk ekspertvurdering</p>
              </div>
            </div>
            <button
              onClick={runAiAnalysis}
              disabled={aiAnalyzing}
              data-testid="run-ai-analysis-btn"
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {aiAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyserer...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Kjør AI-analyse
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* AI Results */}
      {aiResult && !aiResult.error && showAiResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[var(--text-main)] flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI-analyse
            </h3>
            <button
              onClick={() => setShowAiResult(!showAiResult)}
              className="text-sm text-[var(--copper)] hover:underline"
            >
              {showAiResult ? 'Skjul' : 'Vis'} detaljer
            </button>
          </div>
          
          {/* AI Score */}
          <div className={`p-4 rounded-lg border ${
            aiResult.score === 'KRITISK' ? 'bg-red-500/10 border-red-500/30' :
            aiResult.score === 'ADVARSEL' ? 'bg-yellow-500/10 border-yellow-500/30' :
            'bg-[var(--success)]/10 border-[var(--success)]/30'
          }`}>
            <div className="flex items-center gap-3">
              {aiResult.score === 'KRITISK' ? (
                <XCircle className="w-6 h-6 text-red-500" />
              ) : aiResult.score === 'ADVARSEL' ? (
                <AlertTriangle className="w-6 h-6 text-yellow-500" />
              ) : (
                <CheckCircle className="w-6 h-6 text-[var(--success)]" />
              )}
              <div className="flex-1">
                <p className="font-bold text-[var(--text-main)]">
                  {aiResult.score === 'KRITISK' ? 'Kritiske problemer funnet' :
                   aiResult.score === 'ADVARSEL' ? 'Advarsler - bør vurderes' :
                   'Dokumentet ser bra ut'}
                </p>
                <p className="text-sm text-[var(--text-muted)]">{aiResult.sammendrag}</p>
              </div>
            </div>
          </div>
          
          {/* AI Problems */}
          {aiResult.problemer?.length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--text-main)] mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                AI-identifiserte problemer ({aiResult.problemer.length})
              </h4>
              <div className="space-y-3">
                {aiResult.problemer.map((problem, i) => (
                  <FindingCard
                    key={i}
                    finding={{
                      title: problem.tittel,
                      severity: problem.alvorlighet?.toLowerCase(),
                      explanation: problem.forklaring,
                      lawRef: problem.lovref,
                      context: problem.kontekst,
                      recommendation: problem.anbefaling
                    }}
                    expanded={expandedItems[`ai-${i}`]}
                    onToggle={() => toggleExpanded(`ai-${i}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* AI Error */}
      {aiResult?.error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex gap-2">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-400">AI-analyse feilet</p>
              <p className="text-sm text-[var(--text-muted)]">{aiResult.error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Local Results */}
      {localResult && !localResult.error && (
        <div className="space-y-4">
          {/* Header with copy */}
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-[var(--text-main)] flex items-center gap-2">
              <Scale className="w-5 h-5 text-[var(--copper)]" />
              Lokal regelbasert analyse
            </h3>
            <button
              onClick={copyResults}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[var(--bg-highlight)] hover:bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-[var(--success)]" />
                  <span className="text-[var(--success)]">Kopiert!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 text-[var(--text-muted)]" />
                  <span className="text-[var(--text-muted)]">Kopier</span>
                </>
              )}
            </button>
          </div>
          
          {/* Score */}
          <ScoreDisplay score={localResult.score} stats={localResult.stats} />
          
          {/* Positive elements */}
          {localResult.positives.length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--text-main)] mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[var(--success)]" />
                Positive elementer funnet
              </h4>
              <div className="flex flex-wrap gap-2">
                {localResult.positives.map((elem, i) => (
                  <span key={i} className="px-2 py-1 bg-[var(--success)]/10 text-[var(--success)] rounded text-sm">
                    {elem}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Findings */}
          {localResult.findings.length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--text-main)] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Identifiserte problemer ({localResult.findings.length})
              </h4>
              
              <div className="space-y-3">
                {localResult.findings.map((finding, i) => (
                  <FindingCard
                    key={i}
                    finding={{
                      title: finding.tittel || finding.title,
                      severity: finding.alvorlighet || finding.severity,
                      explanation: finding.forklaring || finding.explanation,
                      lawRef: finding.lovref || finding.lawRef,
                      context: finding.kontekst || finding.context,
                      recommendation: finding.anbefaling || finding.recommendation
                    }}
                    expanded={expandedItems[finding.id || `local-${i}`]}
                    onToggle={() => toggleExpanded(finding.id || `local-${i}`)}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg p-4">
              <h4 className="font-medium text-[var(--text-main)] mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--copper)]" />
                Anbefalinger
              </h4>
              <ul className="space-y-2 text-sm text-[var(--text-muted)]">
                {recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[var(--copper)] rounded-full mt-1.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* New analysis button */}
          <button
            onClick={reset}
            className="w-full py-3 bg-[var(--bg-highlight)] hover:bg-[var(--bg-surface)] text-[var(--text-main)] font-medium rounded-lg border border-[var(--border-subtle)] transition-colors flex items-center justify-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Analyser nytt dokument
          </button>
        </div>
      )}
      
      {/* Error */}
      {localResult?.error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex gap-2">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-400">Kunne ikke analysere fil</p>
              <p className="text-sm text-[var(--text-muted)]">{localResult.error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentAnalyzer;
